// Report — the client-facing report as a LIVE page, Curro-branded: every
// figure is computed from the current published run (or the admin-pinned
// preview run) through /api/summary + /api/breakdown, and tenant users see it
// force-scoped to their slice. Charts are dependency-free SVG/CSS (Charts.jsx).

import { useEffect, useState } from "react";
import { AnalyticsApi } from "../hooks/useApi.js";
import { useRun } from "../hooks/useRun.jsx";
import { CountUp, Donut, HBars, Timeline } from "../components/Charts.jsx";
import Icon from "../components/Icon.jsx";
import { fmtR, fmtRm, fmtN } from "../lib/format.js";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthName = (k) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(k));
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : String(k);
};

export default function Report() {
  const { runId } = useRun();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);   // eslint-disable-line react-hooks/set-state-in-effect
    const filter = {};
    Promise.all([
      AnalyticsApi.summary(filter, runId),
      AnalyticsApi.breakdown("category", filter, runId),
      AnalyticsApi.breakdown("campus", filter, runId),
      AnalyticsApi.breakdown("source", filter, runId),
      AnalyticsApi.breakdown("month", filter, runId),
      AnalyticsApi.breakdown("vendor", filter, runId),
      AnalyticsApi.meta(runId),
    ])
      .then(([summary, byCategory, byCampus, bySource, byMonth, byVendor, meta]) =>
        setData({ summary, byCategory, byCampus, bySource, byMonth, byVendor, meta }))
      .catch((e) => setError(e.body?.error ?? e.message));
  }, [runId]);

  if (error) return <div className="error"><Icon name="alert" size={16} /> {error}</div>;
  if (!data) return <p className="hint">Building the report from the published snapshot…</p>;

  const { summary, byCategory, byCampus, bySource, byMonth, byVendor, meta } = data;

  // live insight figures (each guards against missing data)
  const uncategorised = byCategory.find((c) => c.key === "(none)" || /^uncat/i.test(c.key));
  const uncatPct = uncategorised && summary.netSpend ? (uncategorised.spend / summary.netSpend) * 100 : null;
  const topCampus = byCampus[0];
  const dated = byMonth.filter((m) => m.key !== "(none)");
  const peakMonth = dated.length ? dated.reduce((a, b) => (b.spend > a.spend ? b : a)) : null;
  const topVendor = byVendor[0];
  const top10Vendor = byVendor.slice(0, 10).reduce((s, r) => s + r.spend, 0);

  return (
    <div className="report rise">
      {/* hero */}
      <div className="report-hero">
        <div className="deco" />
        <div className="eyebrow">Procurement Advisory{runId ? ` · Preview — snapshot #${runId}` : ""}</div>
        <h1>Procurement &amp; Spend Analysis</h1>
        <p>A live read of {fmtN(summary.lines)} transaction lines across {meta.campuses.length} campuses
          ({meta.dateMin} → {meta.dateMax}). All figures are net of credits, computed from the published
          snapshot{runId ? ` (pinned run #${runId})` : ""} and reflect your access scope.</p>
        <div className="hero-kpis">
          <div className="hk"><div className="v"><CountUp value={summary.netSpend} fmt={fmtR} /></div><div className="l">Total net spend</div></div>
          <div className="hk"><div className="v"><CountUp value={summary.lines} fmt={fmtN} /></div><div className="l">Transaction lines</div></div>
          <div className="hk"><div className="v"><CountUp value={summary.avgLine} fmt={fmtR} /></div><div className="l">Avg line value</div></div>
          <div className="hk"><div className="v"><CountUp value={meta.campuses.length} fmt={fmtN} /></div><div className="l">Campuses</div></div>
        </div>
      </div>

      {/* 01 — where money goes */}
      <section className="report-section">
        <div className="eyebrow">01 · Where the money goes</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Spend by expense category</h2>
        <div className="viz-grid two">
          <div className="viz-card">
            <h3>Top categories</h3>
            <div className="sub">Net spend, largest first</div>
            <HBars rows={byCategory} topN={8} />
          </div>
          <div className="viz-card">
            <h3>Share of total</h3>
            <div className="sub">Top 8 + others</div>
            <Donut rows={byCategory} topN={8} centerTop={fmtRm(summary.netSpend)} centerSub="total net" />
          </div>
        </div>
        {uncategorised && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big"><CountUp value={uncatPct} fmt={(v) => v.toFixed(1) + "%"} /></div>
            <div className="txt"><b>{fmtR(uncategorised.spend)}</b> of spend is <b>uncategorised</b> — mostly journal
              entries. This is the single biggest governance opportunity: classifying it would sharpen every view here.</div>
          </div>
        )}
      </section>

      {/* 02 — benchmarking */}
      <section className="report-section">
        <div className="eyebrow">02 · Benchmarking</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Campus comparison</h2>
        <div className="viz-grid two">
          <div className="viz-card">
            <h3>Net spend by campus</h3>
            <div className="sub">{meta.campuses.length} campuses, largest first</div>
            <HBars rows={byCampus} topN={12} />
          </div>
          <div className="viz-card">
            <h3>Share of spend by campus</h3>
            <div className="sub">Top campuses + others</div>
            <Donut rows={byCampus} topN={8} centerTop={String(meta.campuses.length)} centerSub="campuses" />
          </div>
        </div>
        {topCampus && summary.netSpend > 0 && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big">{((topCampus.spend / summary.netSpend) * 100).toFixed(1)}%</div>
            <div className="txt"><b>{topCampus.key}</b> carries the largest share of network spend —
              {" "}{fmtR(topCampus.spend)} across {fmtN(topCampus.lines)} lines.</div>
          </div>
        )}
      </section>

      {/* 03 — process & control */}
      <section className="report-section">
        <div className="eyebrow">03 · Process &amp; control</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Spend by procurement method</h2>
        <div className="viz-grid two">
          <div className="viz-card">
            <h3>Method mix</h3>
            <div className="sub">How spend enters the system</div>
            <Donut rows={bySource} topN={7} centerTop={String(bySource.length)} centerSub="methods" />
          </div>
          <div className="viz-card">
            <h3>Net spend per method</h3>
            <div className="sub">Largest channels first</div>
            <HBars rows={bySource} topN={9} colorByIndex />
          </div>
        </div>
      </section>

      {/* 04 — seasonality */}
      <section className="report-section">
        <div className="eyebrow">04 · Seasonality</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Monthly spending trend</h2>
        <div className="viz-card">
          <h3>Net spend by month <span className="hint" style={{ fontWeight: 400 }}>· R millions</span></h3>
          <div className="sub">Undated budget-allocation lines excluded</div>
          <Timeline rows={byMonth} />
        </div>
        {peakMonth && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big">{fmtRm(peakMonth.spend)}</div>
            <div className="txt"><b>{monthName(peakMonth.key)}</b> is the peak month at {fmtR(peakMonth.spend)} across
              {" "}{fmtN(peakMonth.lines)} lines. Plan cash and approvals around it.</div>
          </div>
        )}
      </section>

      {/* 05 — supply base */}
      <section className="report-section">
        <div className="eyebrow">05 · Supply base</div>
        <h2 className="section-title" style={{ marginTop: 4 }}>Vendor concentration</h2>
        <div className="viz-grid two">
          {topVendor && (
            <div className="viz-card">
              <h3>Concentration</h3>
              <div className="sub">Top vendor vs the rest of the supply base</div>
              <Donut
                rows={[
                  { key: topVendor.key, spend: topVendor.spend },
                  { key: "Next 9 vendors", spend: Math.max(0, top10Vendor - topVendor.spend) },
                  { key: "All other vendors", spend: Math.max(0, summary.netSpend - top10Vendor) },
                ]}
                topN={3}
                palette={["#0072CE", "#7C5CCB", "#9AA7C7"]}
                centerTop={fmtN(summary.vendors)} centerSub="vendors" />
            </div>
          )}
          <div className="viz-card">
            <h3>Top vendors</h3>
            <div className="sub">By net spend</div>
            <HBars rows={byVendor} topN={10} />
          </div>
        </div>
        {topVendor && summary.netSpend > 0 && (
          <div className="callout" style={{ marginTop: 20 }}>
            <div className="big">{((topVendor.spend / summary.netSpend) * 100).toFixed(1)}%</div>
            <div className="txt"><b>{topVendor.key}</b> alone is {fmtR(topVendor.spend)} —
              {" "}{((topVendor.spend / summary.netSpend) * 100).toFixed(1)}% of all spend across just
              {" "}{fmtN(topVendor.lines)} lines. A key concentration to monitor.</div>
          </div>
        )}
      </section>

      <footer className="report-foot">
        <Icon name="shield" size={15} />
        Generated live from the published snapshot · figures reflect your access scope · The Media Crowd for Curro
      </footer>
    </div>
  );
}
